import dayjs from "dayjs";
import { doc, getDoc, increment, updateDoc, Firestore } from "firebase/firestore";
import { TimeEvent } from "../types/employee";
import { calculateWorkedMinutesWithOvernights } from "./utils";

type DayTotals = Record<string, number>;

const getDateWindow = (date: string) => {
    const previousDate = dayjs(date).subtract(1, "day").format("YYYY-MM-DD");
    const nextDate = dayjs(date).add(1, "day").format("YYYY-MM-DD");
    return { previousDate, date, nextDate };
};

const getTotalsForDateWindow = async (
    db: Firestore,
    userId: string,
    date: string
): Promise<{ previousDate: string; date: string; nextDate: string; totals: DayTotals }> => {
    const { previousDate, nextDate } = getDateWindow(date);
    const refs = [
        doc(db, "time_entries", `${userId}_${previousDate}`),
        doc(db, "time_entries", `${userId}_${date}`),
        doc(db, "time_entries", `${userId}_${nextDate}`),
    ];

    const snaps = await Promise.all(refs.map((ref) => getDoc(ref)));
    const events: TimeEvent[] = [];

    snaps.forEach((snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        const dayEvents: TimeEvent[] = Array.isArray(data.events) ? data.events : [];
        events.push(...dayEvents);
    });

    const minutesByDay = calculateWorkedMinutesWithOvernights(events);
    return {
        previousDate,
        date,
        nextDate,
        totals: {
            [previousDate]: minutesByDay[previousDate] ?? 0,
            [date]: minutesByDay[date] ?? 0,
            [nextDate]: minutesByDay[nextDate] ?? 0,
        },
    };
};

const calculateDeltaForYear = (
    oldTotals: DayTotals,
    newTotals: DayTotals,
    dates: string[],
    targetYear: string
) => {
    let delta = 0;

    dates.forEach((date) => {
        if (!date.startsWith(targetYear)) return;
        delta += (newTotals[date] ?? 0) - (oldTotals[date] ?? 0);
    });

    return Math.round(delta);
};

export const updateAccruedMinutesForDate = async (
    db: Firestore,
    userId: string,
    date: string,
    updateFn: () => Promise<void>
) => {
    const targetYear = dayjs(date).format("YYYY");
    const { previousDate, date: currentDate, nextDate, totals: oldTotals } =
        await getTotalsForDateWindow(
        db,
        userId,
        date
    );

    await updateFn();

    const { totals: newTotals } = await getTotalsForDateWindow(db, userId, date);
    const delta = calculateDeltaForYear(oldTotals, newTotals, [
        previousDate,
        currentDate,
        nextDate,
    ], targetYear);

    if (delta !== 0) {
        await updateDoc(doc(db, "employees", userId), {
            accruedMinutesYTD: increment(delta),
        });
    }
};
